import { Plus, Trash2 } from "lucide-react";
import React from "react";
import { getUniqueId } from "../../../../../../../helpers";
import {
  WallDashPattern,
  WallDoor,
  WallLineCap,
  WallShape,
  WallWindow
} from "../../../../../types";
import { EditorField, EditorFieldGrid } from "../../../../ui/EditorField";
import { IconButton } from "../../../../ui/IconButton";
import { PanelSection } from "../../../../ui/PanelShell";
import { Seg } from "../../../../ui/Seg";
import { useMapCanvas } from "../../../contexts/MapCanvasContext";
import { useMapEditorActions } from "../../../contexts/MapEditorContext";
import { DEFAULT_DOOR_WIDTH, DEFAULT_WINDOW_WIDTH } from "../../../defines";
import { DragNumberInput } from "../../controls/ScrollDragNumberInput";
import { ShapeSectionOptions } from "../ShapePanel/ShapePanel";

const actionButtonClass =
  "flex h-7 items-center justify-center rounded-md border border-neutral-200 px-2 text-xs text-neutral-700 hover:bg-neutral-50";

export const WallPanelControls: React.FC<ShapeSectionOptions> = ({
  shapes
}) => {
  const { updateShape } = useMapEditorActions();
  const { pixelsPerMeter, setPixelsPerMeter } = useMapCanvas();
  const shape = shapes[0] as WallShape;

  const wallPixelLength = Math.round(
    Math.hypot(shape.x2 - shape.x, shape.y2 - shape.y)
  );
  const [calibrationMeters, setCalibrationMeters] = React.useState("");

  const handleCalibrationSubmit = () => {
    const meters = Number(calibrationMeters);
    if (!calibrationMeters.trim() || !Number.isFinite(meters) || meters <= 0) {
      return;
    }
    setPixelsPerMeter(wallPixelLength / meters);
    setCalibrationMeters("");
  };

  const handleThicknessChange = (value: number | string) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        thickness: Number(value)
      } as Partial<WallShape>);
    });
  };

  const handleDashPatternChange = (value: WallDashPattern) => {
    shapes.forEach((s) => {
      updateShape(s.id, { dashPattern: value } as Partial<WallShape>);
    });
  };

  const handleLineCapStartChange = (value: WallLineCap) => {
    shapes.forEach((s) => {
      updateShape(s.id, { lineCapStart: value } as Partial<WallShape>);
    });
  };

  const handleLineCapEndChange = (value: WallLineCap) => {
    shapes.forEach((s) => {
      updateShape(s.id, { lineCapEnd: value } as Partial<WallShape>);
    });
  };

  const handleAddDoor = () => {
    const newDoor: WallDoor = {
      id: `door-${getUniqueId()}`,
      position: 0.5,
      width: DEFAULT_DOOR_WIDTH
    };
    const doors = [...(shape.doors || []), newDoor];
    updateShape(shape.id, { doors } as Partial<WallShape>);
  };

  const handleRemoveDoor = (doorId: string) => {
    const doors = (shape.doors || []).filter((d) => d.id !== doorId);
    updateShape(shape.id, { doors } as Partial<WallShape>);
  };

  const handleDoorPositionChange = (doorId: string, value: number | string) => {
    const doors = (shape.doors || []).map((d) =>
      d.id === doorId ? { ...d, position: Number(value) / 100 } : d
    );
    updateShape(shape.id, { doors } as Partial<WallShape>);
  };

  const handleCurvatureChange = (value: number | string) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        curvature: Number(value)
      } as Partial<WallShape>);
    });
  };

  const handleCurvatureCenterChange = (value: number | string) => {
    shapes.forEach((s) => {
      updateShape(s.id, {
        curvatureCenter: Number(value) / 100
      } as Partial<WallShape>);
    });
  };

  const handleDoorWidthChange = (doorId: string, value: number | string) => {
    const doors = (shape.doors || []).map((d) =>
      d.id === doorId ? { ...d, width: Number(value) } : d
    );
    updateShape(shape.id, { doors } as Partial<WallShape>);
  };

  const handleDoorTransparentToggle = (doorId: string) => {
    const doors = (shape.doors || []).map((d) =>
      d.id === doorId ? { ...d, transparent: !d.transparent } : d
    );
    updateShape(shape.id, { doors } as Partial<WallShape>);
  };

  const handleAddWindow = () => {
    const newWindow: WallWindow = {
      id: `window-${getUniqueId()}`,
      position: 0.5,
      width: DEFAULT_WINDOW_WIDTH
    };
    const windows = [...(shape.windows || []), newWindow];
    updateShape(shape.id, { windows } as Partial<WallShape>);
  };

  const handleRemoveWindow = (windowId: string) => {
    const windows = (shape.windows || []).filter((w) => w.id !== windowId);
    updateShape(shape.id, { windows } as Partial<WallShape>);
  };

  const handleWindowPositionChange = (
    windowId: string,
    value: number | string
  ) => {
    const windows = (shape.windows || []).map((w) =>
      w.id === windowId ? { ...w, position: Number(value) / 100 } : w
    );
    updateShape(shape.id, { windows } as Partial<WallShape>);
  };

  const handleWindowWidthChange = (
    windowId: string,
    value: number | string
  ) => {
    const windows = (shape.windows || []).map((w) =>
      w.id === windowId ? { ...w, width: Number(value) } : w
    );
    updateShape(shape.id, { windows } as Partial<WallShape>);
  };

  return (
    <>
      <PanelSection label="Calibration">
        <div className="flex flex-col gap-1.5">
          <p className="text-neutral-500 text-xs">
            This wall is {wallPixelLength}px long. Enter its real-world
            length to calibrate the map's scale (used for zone areas).
          </p>
          <div className="flex gap-1.5">
            <EditorField
              label="Real-world length (m)"
              type="number"
              min={0}
              step="any"
              value={calibrationMeters}
              onChange={(e) => setCalibrationMeters(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleCalibrationSubmit();
              }}
              placeholder="e.g. 4.5"
              containerClassName="flex-1"
            />
            <button
              type="button"
              onClick={handleCalibrationSubmit}
              className={actionButtonClass}
            >
              Set
            </button>
          </div>
          {pixelsPerMeter ? (
            <div className="flex items-center justify-between">
              <span className="text-neutral-500 text-xs">
                Calibrated: {pixelsPerMeter.toFixed(1)}px = 1m
              </span>
              <button
                type="button"
                onClick={() => setPixelsPerMeter(null)}
                className="text-[11px] text-neutral-400 underline hover:text-neutral-600"
              >
                Clear
              </button>
            </div>
          ) : null}
        </div>
      </PanelSection>

      <PanelSection label="Thickness">
        <div className="flex flex-col gap-1.5">
          <DragNumberInput
            size={"sm"}
            value={shape.thickness || 16}
            onChange={handleThicknessChange}
            min={1}
            max={100}
          />
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => handleThicknessChange(16)}
              className={`${actionButtonClass} flex-1`}
            >
              Internal (16)
            </button>
            <button
              type="button"
              onClick={() => handleThicknessChange(32)}
              className={`${actionButtonClass} flex-1`}
            >
              External (32)
            </button>
          </div>
        </div>
      </PanelSection>

      <PanelSection label="Line Style">
        <Seg
          label="Line Style"
          value={shape.dashPattern || "solid"}
          onValueChange={handleDashPatternChange}
          options={[
            { value: "solid", label: "Solid" },
            { value: "dashed", label: "Dashed" },
            { value: "dotted", label: "Dotted" }
          ]}
        />
      </PanelSection>

      <PanelSection label="Caps">
        <EditorFieldGrid>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Start Cap</span>
            <Seg
              label="Start Cap"
              value={shape.lineCapStart || "butt"}
              onValueChange={handleLineCapStartChange}
              options={[
                { value: "butt", label: "Butt" },
                { value: "round", label: "Round" }
              ]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">End Cap</span>
            <Seg
              label="End Cap"
              value={shape.lineCapEnd || "butt"}
              onValueChange={handleLineCapEndChange}
              options={[
                { value: "butt", label: "Butt" },
                { value: "round", label: "Round" }
              ]}
            />
          </div>
        </EditorFieldGrid>
      </PanelSection>

      <PanelSection label="Curvature">
        <EditorFieldGrid>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Curvature</span>
            <DragNumberInput
              size={"sm"}
              value={shape.curvature || 0}
              onChange={handleCurvatureChange}
              min={-500}
              max={500}
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-600 text-xs">Center (%)</span>
            <DragNumberInput
              size={"sm"}
              value={Math.round((shape.curvatureCenter ?? 0.5) * 100)}
              onChange={handleCurvatureCenterChange}
              min={5}
              max={95}
            />
          </div>
        </EditorFieldGrid>
      </PanelSection>

      <PanelSection label="Doors">
        <div className="flex flex-col gap-2">
          {(shape.doors || []).map((door, index) => (
            <div
              key={door.id}
              className="flex flex-col gap-2 rounded-md border border-neutral-200 p-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 text-xs">
                  Door {index + 1}
                </span>
                <IconButton
                  label="Remove door"
                  size="sm"
                  isDestructive
                  onClick={() => handleRemoveDoor(door.id)}
                >
                  <Trash2 size={14} />
                </IconButton>
              </div>
              <EditorFieldGrid>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Position (%)</span>
                  <DragNumberInput
                    size={"sm"}
                    value={Math.round(door.position * 100)}
                    onChange={(v) => handleDoorPositionChange(door.id, v)}
                    min={0}
                    max={100}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Width (px)</span>
                  <DragNumberInput
                    size={"sm"}
                    value={door.width}
                    onChange={(v) => handleDoorWidthChange(door.id, v)}
                    min={5}
                    max={200}
                  />
                </div>
              </EditorFieldGrid>
              <label className="flex cursor-pointer items-center gap-2 text-neutral-600 text-xs">
                <input
                  type="checkbox"
                  checked={!!door.transparent}
                  onChange={() => handleDoorTransparentToggle(door.id)}
                  className="rounded"
                />
                Transparent
              </label>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddDoor}
            className={`${actionButtonClass} gap-1`}
          >
            <Plus size={12} />
            Add
          </button>
        </div>
      </PanelSection>

      <PanelSection label="Windows">
        <div className="flex flex-col gap-2">
          {(shape.windows || []).map((win, index) => (
            <div
              key={win.id}
              className="flex flex-col gap-2 rounded-md border border-neutral-200 p-2"
            >
              <div className="flex items-center justify-between">
                <span className="text-neutral-600 text-xs">
                  Window {index + 1}
                </span>
                <IconButton
                  label="Remove window"
                  size="sm"
                  isDestructive
                  onClick={() => handleRemoveWindow(win.id)}
                >
                  <Trash2 size={14} />
                </IconButton>
              </div>
              <EditorFieldGrid>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Position (%)</span>
                  <DragNumberInput
                    size={"sm"}
                    value={Math.round(win.position * 100)}
                    onChange={(v) => handleWindowPositionChange(win.id, v)}
                    min={0}
                    max={100}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-neutral-600 text-xs">Width (px)</span>
                  <DragNumberInput
                    size={"sm"}
                    value={win.width}
                    onChange={(v) => handleWindowWidthChange(win.id, v)}
                    min={5}
                    max={200}
                  />
                </div>
              </EditorFieldGrid>
            </div>
          ))}
          <button
            type="button"
            onClick={handleAddWindow}
            className={`${actionButtonClass} gap-1`}
          >
            <Plus size={12} />
            Add
          </button>
        </div>
      </PanelSection>
    </>
  );
};
